import { useCallback } from 'react'

import { cn, focusRing } from '@/shared/ui/cn'

export interface FilterChipOption {
  readonly value: string
  readonly label: string
}

/**
 * A row of choice chips in the filter sidebar, from the mockups' `find-search/FilterChipGroup.jsx`:
 * toggle buttons in a labeled group, the chosen ones filled primary and the rest outlined. With
 * `isMultiple`, any number may be chosen; otherwise exactly one is.
 */
export function FilterChipGroup(props: {
  readonly label: string
  readonly options: readonly FilterChipOption[]
  readonly chosen: readonly string[]
  readonly onChange: (chosen: readonly string[]) => void
  readonly isMultiple?: boolean
}) {
  return (
    <fieldset className="flex min-w-0 flex-wrap gap-2">
      <legend className="sr-only">{props.label}</legend>
      {props.options.map((option) => (
        <ChipButton key={option.value} option={option} chosen={props.chosen} isMultiple={props.isMultiple === true} onChange={props.onChange} />
      ))}
    </fieldset>
  )
}

function ChipButton(props: {
  readonly option: FilterChipOption
  readonly chosen: readonly string[]
  readonly isMultiple: boolean
  readonly onChange: (chosen: readonly string[]) => void
}) {
  const { option, chosen, isMultiple, onChange } = props
  const isChosen = chosen.includes(option.value)
  const press = useCallback(() => {
    if (!isMultiple) {
      onChange([option.value])
    } else {
      onChange(isChosen ? chosen.filter((value) => value !== option.value) : [...chosen, option.value])
    }
  }, [isMultiple, isChosen, chosen, option.value, onChange])

  return (
    <button
      type="button"
      aria-pressed={isChosen}
      onClick={press}
      className={cn(
        'inline-flex min-h-[29px] cursor-pointer items-center rounded-full border px-4 font-sans text-sm font-normal leading-[1.2] transition',
        isChosen
          ? 'border-primary-main bg-primary-main text-primary-contrast hover:border-primary-dark hover:bg-primary-dark'
          : 'border-border-strong bg-transparent text-soft-grey-contrast hover:border-hard-grey-main',
        focusRing,
      )}
    >
      {option.label}
    </button>
  )
}
